import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { EpocusMembershipStatus, EpocusUserStatus } from "elephant-web/gql";
import {
  EPOCUS_STATUS_COMPLETE_BG,
  EPOCUS_STATUS_COMPLETE_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { roleLabelForRole } from "../../settings/users/_lib/program-user-roles";
import type { EpocusAdminProgramMembersQuery } from "./__generated__/epocus-admin-operations.gql";
import { AdminList, AdminListRow } from "./admin-list";

type ProgramMember =
  EpocusAdminProgramMembersQuery["epocusProgramMembers"][number];

const fullName = (member: ProgramMember): string =>
  [member.user.firstName, member.user.lastName].filter(Boolean).join(" ") ||
  "—";

const CardStack = styled(Box)({
  [epocusTheme.breakpoints.up("sm")]: {
    display: "none",
  },
});

const MemberRow = styled(AdminListRow)({
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
});

const CardBody = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
});

const CardName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const CardDetail = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const TableWrap = styled(Box)({
  display: "none",
  "& table": {
    tableLayout: "fixed",
  },
  "& td": {
    wordBreak: "break-word",
  },
  [epocusTheme.breakpoints.up("sm")]: {
    display: "block",
  },
});

const DisabledChip = styled("span")({
  alignSelf: "flex-start",
  display: "inline-block",
  backgroundColor: EPOCUS_STATUS_COMPLETE_BG,
  color: EPOCUS_STATUS_COMPLETE_TEXT,
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: rem(11),
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
});

const VisuallyHidden = styled("span")({
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
});

const StatusCell: FC<{ member: ProgramMember }> = ({ member }) => {
  if (member.user.status === EpocusUserStatus.Disabled) {
    return <DisabledChip>User disabled</DisabledChip>;
  }
  if (member.status === EpocusMembershipStatus.Disabled) {
    return <DisabledChip>Disabled</DisabledChip>;
  }
  return null;
};

export const ProgramMembersList: FC<{
  members: ReadonlyArray<ProgramMember>;
}> = ({ members }) => (
  <AdminList>
    <CardStack>
      {members.map((member) => (
        <MemberRow key={member.id}>
          <CardBody>
            <CardName>{fullName(member)}</CardName>
            <CardDetail>{roleLabelForRole(member.role)}</CardDetail>
            <CardDetail>{member.user.email}</CardDetail>
            <CardDetail>{member.user.telephone || "—"}</CardDetail>
          </CardBody>
          <StatusCell member={member} />
        </MemberRow>
      ))}
    </CardStack>

    <TableWrap>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "17%" }}>Name</TableCell>
            <TableCell style={{ width: "15%" }}>Role</TableCell>
            <TableCell style={{ width: "33%" }}>Email</TableCell>
            <TableCell style={{ width: "20%" }}>Mobile number</TableCell>
            <TableCell style={{ width: "15%" }}>
              <VisuallyHidden>Status</VisuallyHidden>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell>{fullName(member)}</TableCell>
              <TableCell>{roleLabelForRole(member.role)}</TableCell>
              <TableCell>{member.user.email}</TableCell>
              <TableCell style={{ whiteSpace: "nowrap" }}>
                {member.user.telephone || "—"}
              </TableCell>
              <TableCell align="right">
                <StatusCell member={member} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableWrap>
  </AdminList>
);
